import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { SecurityService } from '@wsphere/staff/web';
import { Claim, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { FeatureFlagsService } from '../../../policy/services/feature-flags.service';
import { CLAIM_CLOSE_CONFIG } from '../../config/claim-close.config';
import { ClaimIntakeCardComponent } from '../../components/claim-intake-card/claim-intake-card.component';
import { CLAIM_WORKSPACE_CONFIG } from '../../config/claim-workspace.config';
import { ClaimService } from '../../services';
import { getJobActions, getPaymentHolds, hasOnlyFinishedJobs, JOB_ACTION_ROUTES, JobAction } from '../../utils';

type LoadState = 'loading' | 'ready' | 'failed' | 'forbidden';

/** One page to work a claim; its context stays pinned. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-workspace',
  templateUrl: './claim-workspace.page.html',
  standalone: false,
})
export class ClaimWorkspacePage implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private securitySvc: SecurityService,
    private featureFlagsSvc: FeatureFlagsService,
    private toastSvc: ToastService
  ) {}

  @ViewChild(ClaimIntakeCardComponent) intakeCard?: ClaimIntakeCardComponent;

  readonly config = CLAIM_WORKSPACE_CONFIG;
  readonly open = Claim.Status.OPEN;
  readonly closeConfig = CLAIM_CLOSE_CONFIG;
  readonly hasOnlyFinishedJobs = hasOnlyFinishedJobs;
  readonly draft = Claim.ClaimItem.Status.DRAFT;
  readonly resolved = Claim.ClaimItem.Status.RESOLVED;
  readonly jobActionOrder: JobAction[] = ['details', 'cure', 'decide', 'resolve'];

  loadState: LoadState = 'loading';
  refreshing = false;
  refreshFailed = false;
  /** A focused dialog (child route) is open over the workspace. */
  hasOpenDialog = false;
  /** The audit trail loads only once it's opened. */
  showAudit = false;

  readonly contactReasonValues = Object.values(Claim.ContactReason);
  /** Picked but not saved yet; undefined means untouched. */
  pendingContactReason: Claim.ContactReason | null | undefined = undefined;
  savingContactReason = false;

  private destroy$ = new Subject<void>();
  private selectedJobId$ = new BehaviorSubject<string | null>(null);

  readonly claim$ = this.claimSvc.getClaim().pipe(shareReplay(1));

  readonly jobs$ = this.claim$.pipe(map(claim => claim?.items ?? []));

  readonly selectedJob$ = combineLatest([this.jobs$, this.selectedJobId$]).pipe(
    map(([jobs, id]) => jobs.find(job => job.id === id) ?? jobs[0] ?? null),
    shareReplay(1)
  );

  private permissionKey$ = this.claim$.pipe(
    map(claim => (claim?.warranty?.distributor?.permissionKey ? [claim.warranty.distributor.permissionKey] : null)),
    shareReplay(1)
  );

  readonly permissions$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => {
      const can = (permission: Parameters<SecurityService['hasAccess']>[0]) =>
        this.securitySvc.hasAccess(permission, permissionKey);
      const [
        manageCoverage,
        manageCure,
        adjudicate,
        manageResolution,
        authorize,
        reverse,
        editClaim,
        adjust,
        manageHolds,
        attachEvidence,
        viewFiles,
        uploadFiles,
        manageOverrides,
        manageAllocations,
        viewAudit,
        closeClaim,
      ] = await Promise.all([
        can('clm_ManageClaimItemCoverage'),
        can('clm_ManageClaimItemCure'),
        can('clm_AdjudicateClaimItem'),
        can('clm_ManageClaimItemResolution'),
        can('clm_AuthorizeClaim'),
        can('clm_ReverseClaimAuthorization'),
        can('clm_EditClaim'),
        can(Claim.ClaimItem.ADJUST_LINE_PERMISSION),
        can(Claim.ClaimItem.MANAGE_HOLDS_PERMISSION),
        can(Claim.ClaimItem.ATTACH_HOLD_EVIDENCE_PERMISSION),
        can('clm_ViewAttachments'),
        can('clm_ManageAttachments'),
        can('clm_ManageOverrides'),
        can('clm_ManageAllocations'),
        can('clm_ViewAudit'),
        can('clm_CloseClaim'),
      ]);

      return {
        manageCoverage,
        manageCure,
        adjudicate,
        manageResolution,
        authorize,
        reverse,
        editClaim,
        adjust,
        manageHolds,
        attachEvidence,
        viewFiles,
        uploadFiles,
        manageOverrides,
        manageAllocations,
        viewAudit,
        closeClaim,
      };
    }),
    shareReplay(1)
  );

  /** Just the count; the entries load once the audit box is opened. */
  readonly auditCount$ = combineLatest([this.claim$, this.permissions$]).pipe(
    switchMap(async ([claim, permissions]) => {
      if (!claim || !permissions.viewAudit) return null;
      const audit = await this.claimSvc.listAudit(claim.id, { limit: 1 }).catch(() => null);
      return audit?.meta.totalItems ?? null;
    }),
    shareReplay(1)
  );

  readonly actions$ = combineLatest([this.claim$, this.selectedJob$, this.permissions$]).pipe(
    map(([claim, job, permissions]) => (claim && job ? getJobActions(claim, job, permissions) : null))
  );

  readonly isZeroPayAllowed$ = this.featureFlagsSvc.isEnabled('CLAIM_ZERO_PAY');

  // Approvals and splits for each active authorization, read like the payments page.
  readonly paymentHolds$ = this.claim$.pipe(
    switchMap(async claim => {
      const isAuthorized =
        !!claim &&
        claim.isAuthorizationRequired !== false &&
        Claim.Authorization.getActiveAuthorizations(claim.authorizations ?? []).length > 0;
      if (!claim || !isAuthorized) return null;

      try {
        const [{ items: overrides }, { items: allocations }] = await Promise.all([
          this.claimSvc.listOverrides(claim.id),
          this.claimSvc.listAllocations(claim.id),
        ]);
        return getPaymentHolds(claim, overrides, allocations);
      } catch {
        // Not fatal; the payments page has the details.
        return null;
      }
    }),
    shareReplay(1)
  );

  readonly paymentReadiness$ = combineLatest([this.claim$, this.paymentHolds$]).pipe(
    map(([claim, holds]) =>
      claim
        ? Claim.getPaymentReadiness(
            claim.authorizationState,
            claim.paymentBalances,
            authorizationId => !!holds?.byAuthorization[authorizationId]?.reason
          )
        : null
    )
  );

  async ngOnInit() {
    const claim = (await firstValueFrom(this.route.data))['claim'] as Claim.Model | null;
    if (!claim) {
      this.loadState = 'failed';
      return;
    }

    const permissionKey = claim.warranty?.distributor?.permissionKey
      ? [claim.warranty.distributor.permissionKey]
      : null;
    if (!(await this.securitySvc.hasAccess('clm_ViewClaim', permissionKey))) {
      this.loadState = 'forbidden';
      return;
    }

    // The workspace is part of the new claims flow; other claims stay on the classic page.
    if (!Claim.Authorization.usesNewFlow(claim)) {
      await this.router.navigate(['/claims', claim.id], { replaceUrl: true });
      return;
    }

    await this.claimSvc.refreshClaim(claim);
    // The service may hold a stale copy (e.g. from the classic page); reload it.
    if ((await firstValueFrom(this.claimSvc.getClaim())) !== claim) await this.refresh();
    this.syncWithRoute();
    this.loadState = 'ready';

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncWithRoute());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectJob(jobId: string) {
    this.selectedJobId$.next(jobId);
    this.router.navigate([], { relativeTo: this.route, queryParams: { job: jobId }, queryParamsHandling: 'merge' });
  }

  openAction(job: Claim.ClaimItem.Model, action: JobAction) {
    this.router.navigate([job.id, JOB_ACTION_ROUTES[action]], {
      relativeTo: this.route,
      queryParams: { job: job.id },
    });
  }

  async refresh() {
    this.refreshing = true;
    this.refreshFailed = false;

    try {
      await this.claimSvc.refreshClaim();
      // Totals update in the background after charge changes.
      this.claimSvc.refreshClaimTotals();
    } catch {
      this.refreshFailed = true;
    } finally {
      this.refreshing = false;
    }
  }

  /** The select echoes values set from code; only a change counts. */
  pickContactReason(value: Claim.ContactReason | null, claim: Claim.Model) {
    this.pendingContactReason = value === claim.contactReason ? undefined : value;
  }

  async saveContactReason(claim: Claim.Model) {
    if (this.pendingContactReason === undefined) return;
    const contactReason = this.pendingContactReason;

    this.savingContactReason = true;
    try {
      await this.claimSvc.updateContactReason(claim.id, { contactReason });
      this.toastSvc.show({
        type: 'success',
        title: contactReason ? this.config.context.contactSaved : this.config.context.contactCleared,
        text: contactReason ? this.config.context.contactReasonLabels[contactReason] : '',
      });
      // Refresh first, or the picker echoes the old value back as a pick.
      await this.refresh();
      this.pendingContactReason = undefined;
    } catch {
      this.toastSvc.show({ type: 'error', title: this.config.context.contactFailed, text: '' });
    } finally {
      this.savingContactReason = false;
    }
  }

  /** A calendar day, compared with today. */
  isRepairOverdue(claim: Claim.Model) {
    return (
      !!claim.repairDeadline && moment.utc(claim.repairDeadline).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD')
    );
  }

  formatAmount(amount: number | null | undefined) {
    return '$' + ((amount ?? 0) / 100).toFixed(2);
  }

  /** Paid and authorized across the first authorization and its supplementals; null when not authorized. */
  paymentTotals(claim: Claim.Model) {
    const balances = claim.paymentBalances ?? [];
    if (!balances.length) return null;

    return balances.reduce(
      (totals, balance) => ({
        paid: totals.paid + balance.paid,
        authorized: totals.authorized + balance.authorizedPayable,
      }),
      { paid: 0, authorized: 0 }
    );
  }

  claimTheme(status: Claim.Status): ThemeColor {
    return status === Claim.Status.OPEN
      ? 'info'
      : status === Claim.Status.CLOSED
        ? 'default'
        : status === Claim.Status.CANCELLED
          ? 'warn'
          : 'danger';
  }

  warrantyTheme(warranty?: Warranty.Model): ThemeColor {
    return warranty?.status === Warranty.Status.ACTIVATED
      ? 'success'
      : warranty?.status === Warranty.Status.EXPIRED
        ? 'danger'
        : warranty?.status === Warranty.Status.CANCELLED
          ? 'warn'
          : 'default';
  }

  /** A job's requested and authorized amounts. */
  jobTotals(claim: Claim.Model, jobId: string) {
    return claim.totals?.jobs.find(job => job.jobId === jobId) ?? null;
  }

  jobTheme(job: Claim.ClaimItem.Model): ThemeColor {
    return Claim.ClaimItem.getTheme(job.status);
  }

  /** Syncs the selected job and dialog state with the URL. */
  private syncWithRoute() {
    const dialog = this.route.snapshot.firstChild;
    const wasDialogOpen = this.hasOpenDialog;
    this.hasOpenDialog = !!dialog;

    // An open dialog's job is the selected one.
    const params = this.route.snapshot.queryParamMap;
    const jobId = (dialog && findParam(dialog, 'itemId')) ?? params.get('job') ?? this.selectedJobId$.value;
    if (jobId !== this.selectedJobId$.value) this.selectedJobId$.next(jobId);

    // Closing a dialog drops the job from the URL; put it back.
    const refreshRequested = params.get('closeItemDialog') === 'true' || params.get('refresh') === 'true';
    if (refreshRequested) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { job: jobId, closeItemDialog: null, refresh: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (refreshRequested || (wasDialogOpen && !dialog)) this.refresh();
  }
}

function findParam(route: ActivatedRouteSnapshot, key: string): string | null {
  return route.params[key] ?? (route.firstChild ? findParam(route.firstChild, key) : null);
}
